import {
  closingRob,
  isDailyKind,
  nextOperationOptions,
  reportHasSection,
  type VoyageReportKind,
  voyageReportNumber,
} from '@felix/domain';
import type {
  Content,
  CustomTableLayout,
  TableCell,
  TDocumentDefinitions,
} from 'pdfmake/interfaces.js';
import { ltUtcText, offsetLabel, zonedStamp } from '../../inputs/lt-utc.js';
import { TOKENS } from '../../tokens.js';
import { type EventKind, eventFields } from './event-form.js';
import { nextOperationKeys } from './next-operation-text.js';
import { formatLatitude, formatLongitude } from './noon-report.js';
import {
  dailyPdfContent,
  noonPdfContent,
  type PdfBuilders,
  type PdfVoyage,
} from './report-pdf-daily.js';
import type { ReportBrand } from './source.js';
import type { VoyageReport } from './types.js';

/** Çevirici: i18next `t`. */
export type ReportT = (key: string, options?: Record<string, unknown>) => string;

export interface VoyageReportPdfInput {
  report: VoyageReport;
  /** Seferin raporları (rapor numarası için). */
  reports: readonly VoyageReport[];
  /** Sefersiz kalkışta `null` (ADR 0058 §3). CP değerleri Noon'un CP bölümü için (ADR 0059 §8). */
  voyage: PdfVoyage | null;
  vessel: { name: string; imo?: string | null };
  brand: ReportBrand | null;
  /** Basım zamanı (UTC ISO). */
  printedAt: string;
}

const FONT_SIZE = 9;
const LINES: CustomTableLayout = {
  hLineWidth: (i) => (i === 1 ? 0.8 : 0.4),
  vLineWidth: () => 0,
  hLineColor: () => TOKENS.border,
  paddingLeft: () => 4,
  paddingRight: () => 4,
  paddingTop: () => 3,
  paddingBottom: () => 3,
  fillColor: (row) => (row === 0 ? TOKENS['table-head-bg'] : null),
};
const FACTS: CustomTableLayout = { ...LINES, fillColor: () => null, hLineWidth: () => 0.4 };

/** Logo yalnız PNG ya da JPEG veri adresiyse basılır (pdfmake; ADR 0057 §7). */
function logoOf(brand: ReportBrand | null): string | null {
  const logo = brand?.logo ?? null;
  return logo && /^data:image\/(png|jpeg);base64,/.test(logo) ? logo : null;
}

const num = (value: unknown, digits = 1) =>
  typeof value === 'number' && Number.isFinite(value) ? value.toFixed(digits) : '—';
const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : '—');
const record = (value: unknown): Record<string, unknown> =>
  value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
const rows = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.map(record) : [];

/**
 * Sefer raporunun ortak PDF'i (ADR 0057 §7; ortağın cevabı #9): A4 dikey, şirket adı ve logo,
 * gemi, sefer, rapor no, tarih LT + UTC, bölümler, onay bilgisi (ad, rol, zaman; imza yerine),
 * her sayfada basım zamanı ve sayfa no. Saf işlev: gemi ve ofis aynı tanımı kullanır; pdfmake
 * istemcide, gömülü yazı tipiyle internetsiz çalışır.
 */
export function voyageReportPdf(t: ReportT, input: VoyageReportPdfInput): TDocumentDefinitions {
  const { report, voyage, vessel } = input;
  const kind = report.kind as VoyageReportKind;
  const offset = report.tzOffsetMin;
  const data = report.data;
  const k = (key: string, options?: Record<string, unknown>) => t(`voyage.pdf.${key}`, options);
  const s = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.sections.${key}`, options);
  const time = (value: unknown) => (typeof value === 'string' ? ltUtcText(value, offset) : '—');
  const title = t(`voyage.kinds.${kind}`);
  // Kalıcı numara (ADR 0058 §5); eski sunucuda sefer numarasından türetilir (ADR 0057 §6)
  const number =
    report.reportNo ?? (voyage ? voyageReportNumber(voyage.voyageNo, report, input.reports) : '');
  const logo = logoOf(input.brand);

  const heading = (value: string): Content => ({ text: value, style: 'section' });
  const facts = (pairs: [string, string][]): Content => {
    const body: TableCell[][] = [];
    for (let i = 0; i < pairs.length; i += 2) {
      const [a, b] = [pairs[i], pairs[i + 1]];
      body.push([
        { text: a?.[0] ?? '', style: 'label' },
        { text: a?.[1] ?? '' },
        { text: b?.[0] ?? '', style: 'label' },
        { text: b?.[1] ?? '' },
      ]);
    }
    return { table: { widths: [90, '*', 90, '*'], body }, layout: FACTS };
  };
  const table = (head: string[], body: string[][], widths: (string | number)[]): Content => ({
    table: {
      headerRows: 1,
      widths,
      body: [head.map((h) => ({ text: h, bold: true })), ...body],
    },
    layout: LINES,
  });

  const content: Content[] = [];
  // Başlık: logo, şirket, rapor türü ve numarası; sağda durum işareti
  const marks: string[] = [];
  if (report.status === 'draft') marks.push(k('draftMark'));
  if (report.status === 'reopened') marks.push(k('reopenedMark'));
  // Gerekçeli iptal (ADR 0077 §3): rapor geçersizdir, izde kalır
  if (report.status === 'void') marks.push(k('voidMark'));
  // Yeniden açılıp tekrar gönderilmiş (ADR 0058 §5)
  if (report.amended) marks.push(k('amended'));
  content.push({
    columns: [
      ...(logo ? [{ image: logo, fit: [48, 48] as [number, number], width: 56 }] : []),
      {
        width: '*',
        stack: [
          { text: input.brand?.name ?? '', style: 'company' },
          { text: title, style: 'title' },
          { text: k('reportNo', { no: number }), style: 'muted' },
        ],
      },
      {
        width: 'auto',
        stack: marks.map((mark) => ({ text: mark, style: 'mark', alignment: 'right' as const })),
      },
    ],
    columnGap: 10,
    margin: [0, 0, 0, 8],
  });

  // Genel bilgiler
  const position =
    report.lat === null || report.lon === null
      ? '—'
      : `${formatLatitude(report.lat)}  ${formatLongitude(report.lon)}`;
  content.push(
    facts([
      [k('vessel'), vessel.imo ? `${vessel.name} (IMO ${vessel.imo})` : vessel.name],
      [k('voyage'), voyage?.voyageNo ?? k('noVoyage')],
      [k('from'), voyage?.fromPort ?? '—'],
      [k('to'), voyage?.toPort ?? '—'],
      [k('reportTimeLt'), `${zonedStamp(report.reportedAt, offset)} (${offsetLabel(offset)})`],
      [k('reportTimeUtc'), `${zonedStamp(report.reportedAt)} UTC`],
      [k('position'), position],
      [k('status'), t(`voyage.event.status.${report.status}`)],
    ]),
  );

  // Ayrıntılar (türün alanları)
  const detail: [string, string][] = [];
  if (kind === 'noon') {
    const nav = record(data.navigation);
    detail.push(
      // Etiketler birimi taşır ("Course (°)", "Speed Over Ground (kn)")
      [t('voyage.noon.navigation.course'), num(nav.course, 0)],
      [t('voyage.noon.navigation.sog'), num(nav.sog)],
      [t('voyage.noon.navigation.stw'), num(nav.stw)],
      [t('voyage.noon.navigation.distanceSinceLast'), num(nav.distanceSinceLast)],
      [t('voyage.noon.navigation.distanceToGo'), num(nav.distanceToGo)],
    );
  } else {
    for (const field of eventFields(kind as EventKind, report.schema)) {
      const value = data[field.key];
      if (value === undefined || value === null || value === '') continue;
      const label = t(`voyage.event.fields.${field.key}`);
      const shown =
        field.type === 'datetime'
          ? time(value)
          : field.type === 'number'
            ? `${num(value, field.decimals ?? 1)}${field.unit ? ` ${field.unit}` : ''}`
            : field.type === 'select' && field.options
              ? t(`voyage.event.options.${field.options.list}.${String(value)}`)
              : text(value);
      detail.push([label, shown]);
    }
    const draft = record(data.draft);
    if (typeof draft.fwdM === 'number' || typeof draft.aftM === 'number') {
      detail.push([t('voyage.event.draft'), `${num(draft.fwdM, 2)} / ${num(draft.aftM, 2)} m`]);
    }
  }
  if (detail.length > 0) content.push(heading(t('voyage.event.details')), facts(detail));

  // Kalkış ve varış bölümleri (sürüm 2; ADR 0058)
  if (report.schema >= 2)
    content.push(
      ...departureArrivalContent(t, report, input.reports, { heading, facts, table, time }),
    );

  // Noon panelleri ve günlük bölümleri (ADR 0059 §8)
  if (kind === 'noon') {
    content.push(
      ...noonPdfContent(t, report, input.reports, voyage, { heading, facts, table, time }),
    );
  }
  if (isDailyKind(kind)) {
    content.push(
      ...dailyPdfContent(t, kind, report, input.reports, { heading, facts, table, time }),
    );
  }

  // Römorkör
  const tugs = rows(data.tugs);
  if (tugs.length > 0 || typeof data.tugDetails === 'string') {
    content.push(heading(s('tugs.title')));
    if (tugs.length > 0) {
      content.push(
        table(
          ['#', s('tugs.name'), s('tugs.bollardPull'), s('tugs.alongside'), s('tugs.castOff')],
          tugs.map((tug, i) => [
            String(i + 1),
            text(tug.name),
            `${num(tug.bollardPullT, 0)} t`,
            time(tug.alongside),
            time(tug.castOff),
          ]),
          [14, '*', 50, 130, 130],
        ),
      );
    }
    if (typeof data.tugDetails === 'string' && data.tugDetails.trim()) {
      content.push({ text: s('tugs.legacy', { text: data.tugDetails }), style: 'muted' });
    }
  }

  // ROB (MT): yakıt, yağ, tatlı su; Noon'da açılış, tüketim, ikmal, kapanış
  const robBody: string[][] = [];
  const today = isDailyKind(kind);
  if (kind === 'noon') {
    const fuel = record(data.fuel);
    for (const row of rows(fuel.fuels)) {
      const closing =
        typeof row.opening === 'number' &&
        typeof row.consumption === 'number' &&
        typeof row.bunkered === 'number'
          ? closingRob({
              opening: row.opening,
              consumption: row.consumption,
              bunkered: row.bunkered,
            })
          : null;
      robBody.push([text(row.fuel), num(row.opening), num(row.consumption), num(closing)]);
    }
    for (const row of rows(fuel.lubes)) {
      robBody.push([text(row.name), num(row.opening), num(row.consumption), '—']);
    }
  } else {
    // Günlükte "bugün" sütunu (ADR 0059 §3)
    const line = (name: string, row: Record<string, unknown>) => [
      name,
      num(row.previousMt),
      num(row.consumptionMt),
      ...(today ? [num(row.todayMt)] : []),
      num(row.rob),
    ];
    for (const row of rows(data.rob)) robBody.push(line(text(row.fuel), row));
    for (const row of rows(data.lubes)) robBody.push(line(s(`lubes.${String(row.lube)}`), row));
    const water = record(data.freshWater);
    if (typeof water.rob === 'number') robBody.push(line(s('rob.freshWater'), water));
  }
  if (robBody.length > 0) {
    content.push(
      heading(s('rob.title')),
      table(
        [
          s('rob.type'),
          `${s('rob.previous')} (MT)`,
          `${today ? s('rob.sinceLast') : s('rob.consumed')} (MT)`,
          ...(today ? [`${s('rob.today')} (MT)`] : []),
          `${s('rob.rob')} (MT)`,
        ],
        robBody,
        today ? ['*', 70, 70, 60, 70] : ['*', 80, 80, 80],
      ),
    );
  }

  // Hava
  const weather = record(data.weather);
  if (kind === 'noon') {
    const wind = record(weather.wind);
    const sea = record(weather.sea);
    if (Object.keys(wind).length > 0 || Object.keys(sea).length > 0) {
      content.push(
        heading(s('weather.title')),
        facts([
          [t('voyage.noon.weather.direction'), num(wind.direction, 0)],
          [s('weather.beaufort'), num(wind.beaufort, 0)],
          [s('weather.seaState'), num(sea.state, 0)],
          [t('voyage.noon.weather.swellHeight'), num(sea.swellHeightM)],
        ]),
      );
    }
  } else if (reportHasSection(kind, 'weather') && Object.keys(weather).length > 0) {
    const scale = (value: unknown, list: string) =>
      typeof value === 'number' ? `${value} – ${s(`${list}.${value}`)}` : '—';
    content.push(
      heading(s('weather.title')),
      facts([
        [s('weather.windDirection'), text(weather.windDirection)],
        [s('weather.beaufort'), scale(weather.beaufort, 'beaufortScale')],
        [s('weather.seaState'), scale(weather.seaState, 'douglasScale')],
        [
          s('weather.visibility'),
          typeof weather.visibility === 'string'
            ? s(`visibilityGrades.${weather.visibility}`)
            : '—',
        ],
        [s('weather.airTemp'), `${num(weather.airTempC)} °C`],
        [s('weather.seaTemp'), `${num(weather.seaTempC)} °C`],
      ]),
    );
  }

  // Kişiler
  const persons = record(data.persons);
  if (Object.keys(persons).length > 0) {
    content.push(
      heading(s('persons.title')),
      facts([
        [s('persons.crew'), num(persons.crew, 0)],
        [s('persons.passengers'), num(persons.passengers, 0)],
      ]),
    );
  }

  // Gemi Durumu basılmaz (04.11 E4, ADR 0161): eski raporlarda veri olsa da

  // Next Operation
  if (nextOperationOptions(kind).length > 0) {
    const next = typeof data.nextOperation === 'string' ? data.nextOperation : null;
    content.push(
      heading(s('next.title')),
      facts([
        [
          s('next.question'),
          next
            ? `${s(nextOperationKeys(kind, next).label)} (${s(nextOperationKeys(kind, next).leadsTo)})`
            : '—',
        ],
      ]),
    );
  }

  // Açıklama
  const remarks = kind === 'noon' ? record(data.navigation).remarks : data.remarks;
  if (typeof remarks === 'string' && remarks.trim()) {
    content.push(heading(t('voyage.event.remarks')), { text: remarks });
  }

  // Onay bilgisi: imza yerine ad, rol ve zaman (ortağın cevabı #9)
  content.push(heading(k('approval')));
  const approval: [string, string][] = report.submittedAt
    ? [
        [k('submittedBy'), report.submittedByName ?? '—'],
        [k('role'), report.submittedByRole ?? '—'],
        [k('submittedAt'), ltUtcText(report.submittedAt, offset)],
        [k('revision'), String(report.revision)],
      ]
    : [[k('submittedBy'), k('notSubmitted')]];
  if (report.status === 'reopened' && report.reopenedAt) {
    approval.push(
      [k('reopenedAt'), ltUtcText(report.reopenedAt, offset)],
      [k('reopenReason'), report.reopenReason ?? '—'],
    );
  }
  if (report.status === 'void' && report.voidedAt) {
    approval.push(
      [k('voidedAt'), ltUtcText(report.voidedAt, offset)],
      [k('voidReason'), report.voidReason ?? '—'],
    );
  }
  content.push(facts(approval), { text: k('approvalNote'), style: 'muted', margin: [0, 4, 0, 0] });

  const printed = k('printed', { at: `${zonedStamp(input.printedAt)} UTC` });
  return {
    pageSize: 'A4',
    pageOrientation: 'portrait',
    pageMargins: [36, 36, 36, 44],
    defaultStyle: { font: 'Roboto', fontSize: FONT_SIZE, color: TOKENS['navy-900'] },
    info: { title: `${title} ${number}`, subject: vessel.name, creator: 'Felixmate' },
    styles: {
      company: { fontSize: 10, bold: true, color: TOKENS['text-secondary'] },
      title: { fontSize: 15, bold: true, margin: [0, 2, 0, 2] },
      section: { fontSize: 10.5, bold: true, margin: [0, 10, 0, 4] },
      label: { color: TOKENS['text-secondary'] },
      muted: { color: TOKENS['text-secondary'], fontSize: 8 },
      mark: { bold: true, color: TOKENS['danger-fg'], fontSize: 9 },
    },
    content,
    footer: (page: number, pages: number) => ({
      columns: [
        {
          text: `${number} · ${printed} · Felixmate`,
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
        {
          text: k('page', { page, pages }),
          alignment: 'right',
          fontSize: 7,
          color: TOKENS['text-secondary'],
        },
      ],
      margin: [36, 12, 36, 0],
    }),
  };
}

type Builders = PdfBuilders;

/**
 * Sürüm 2 Departure, Berthing ve EOSP'un bölümleri (ADR 0058 §2, §6): pilot ve son halat, sonraki
 * liman, gemi durumu, yük, sınıflandırma, sefer kararı, son kontrol, liman araması, NOR, halat;
 * Berthing'de önceki EOSP'un özeti. Boş bölüm basılmaz.
 */
function departureArrivalContent(
  t: ReportT,
  report: VoyageReport,
  reports: readonly VoyageReport[],
  { heading, facts, table, time }: Builders,
): Content[] {
  const s = (key: string, options?: Record<string, unknown>) =>
    t(`voyage.sections.${key}`, options);
  const data = report.data;
  const out: Content[] = [];
  const add = (title: string, pairs: [string, string | null][]) => {
    const filled = pairs.filter((p): p is [string, string] => p[1] !== null && p[1] !== '—');
    if (filled.length > 0) out.push(heading(title), facts(filled));
  };
  const opt = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null);
  const qty = (value: unknown, unit: string, digits = 1) =>
    typeof value === 'number' ? `${num(value, digits)} ${unit}` : null;
  if (report.kind === 'eosp') {
    add(s('eospRef.title'), [[s('eospRef.distanceToPort'), qty(data.distanceToPortNm, 'NM')]]);
  }
  if (report.kind === 'arrival') {
    // EOSP + Berthing tek akış: Berthing çıktısında önceki EOSP'un özeti (ADR 0058 §6)
    const eosp = [...reports]
      .filter((r) => r.kind === 'eosp' && Date.parse(r.reportedAt) <= Date.parse(report.reportedAt))
      .sort((a, b) => Date.parse(a.reportedAt) - Date.parse(b.reportedAt))
      .at(-1);
    if (eosp) {
      const rob = rows(eosp.data.rob)
        .map((r) => `${text(r.fuel)} ${num(r.rob)}`)
        .join(' · ');
      add(s('eospRef.title'), [
        [s('eospRef.time'), time(eosp.reportedAt)],
        [
          s('eospRef.position'),
          eosp.lat !== null && eosp.lon !== null
            ? `${formatLatitude(eosp.lat)}  ${formatLongitude(eosp.lon)}`
            : null,
        ],
        [s('eospRef.distanceToPort'), qty(eosp.data.distanceToPortNm, 'NM')],
        [s('eospRef.rob'), rob || null],
      ]);
    }
  }
  if (report.kind === 'departure') {
    add(s('pilot.title'), [
      [s('pilot.castOff'), time(report.reportedAt)],
      [s('pilot.notUsed'), data.pilotNotUsed === true ? '✓' : null],
      [s('pilot.onBoard'), data.pilotOnBoard ? time(data.pilotOnBoard) : null],
      [s('pilot.off'), data.pilotOff ? time(data.pilotOff) : null],
      [s('pilot.tugsUsed'), typeof data.tugsUsed === 'number' ? String(data.tugsUsed) : null],
    ]);
  }
  if (report.kind === 'departure' || report.kind === 'arrival') {
    add(s('nextPort.title'), [
      [s('nextPort.port'), opt(data.nextPort)],
      [s('nextPort.eta'), data.nextPortEta ? time(data.nextPortEta) : null],
      [s('nextPort.distance'), qty(data.distanceToNextPortNm, 'NM')],
      [s('nextPort.unknownReason'), opt(data.nextPortUnknownReason)],
    ]);
    const draft = record(data.draft);
    add(s('load.title'), [
      [
        s('load.condition'),
        typeof data.loadCondition === 'string'
          ? t(`voyage.event.options.loadConditions.${data.loadCondition}`)
          : null,
      ],
      [
        `${s('load.draftFwd')} / ${s('load.draftAft')}`,
        typeof draft.fwdM === 'number' ? `${num(draft.fwdM, 2)} / ${num(draft.aftM, 2)} m` : null,
      ],
      [s('load.airDraft'), qty(data.airDraftM, 'm', 2)],
      [s('load.displacement'), qty(data.displacementMt, 'MT', 0)],
    ]);
    const nor = record(data.nor);
    add(s('nor.title'), [
      [s('nor.tenderedAt'), nor.tenderedAt ? time(nor.tenderedAt) : null],
      [s('nor.tenderedTo'), opt(nor.tenderedTo)],
      [s('nor.acceptedAt'), nor.acceptedAt ? time(nor.acceptedAt) : null],
      [s('nor.acceptedBy'), opt(nor.acceptedBy)],
      [s('nor.laytime'), nor.laytimeCommencedAt ? time(nor.laytimeCommencedAt) : null],
      [s('nor.remarks'), opt(nor.remarks)],
    ]);
    const mooring = record(data.mooring);
    add(s('mooring.title'), [
      [s('mooring.lines'), typeof mooring.lines === 'number' ? String(mooring.lines) : null],
      [
        s('mooring.shoreLines'),
        typeof mooring.shoreLines === 'number' ? String(mooring.shoreLines) : null,
      ],
      [s('mooring.remarks'), opt(mooring.remarks)],
    ]);
    const cargo = record(data.cargo);
    if (typeof cargo.operation === 'string') {
      out.push(
        heading(s('cargo.title')),
        facts([
          [
            report.kind === 'departure' ? s('cargo.operationDone') : s('cargo.operationPlanned'),
            s(`cargo.operations.${cargo.operation}`),
          ],
          [
            s('cargo.onBoard'),
            typeof cargo.onBoardQty === 'number'
              ? `${num(cargo.onBoardQty, 2)} ${typeof cargo.onBoardUnit === 'string' ? s(`cargo.units.${cargo.onBoardUnit}`) : ''}`
              : '—',
          ],
        ]),
      );
      const lines = rows(cargo.lines);
      if (lines.length > 0) {
        out.push(
          table(
            ['#', s('cargo.type'), s('cargo.loaded'), s('cargo.discharged'), s('cargo.unit')],
            lines.map((line, i) => [
              String(i + 1),
              text(line.cargoType),
              num(line.loadedQty, 2),
              num(line.dischargedQty, 2),
              typeof line.unit === 'string' ? s(`cargo.units.${line.unit}`) : '—',
            ]),
            [14, '*', 70, 70, 50],
          ),
        );
      }
      if (opt(cargo.remarks)) out.push({ text: String(cargo.remarks), margin: [0, 4, 0, 0] });
    }
    const call = record(data.portCall);
    add(s('portCall.title'), [[s('portCall.call'), opt(call.label)]]);
  }
  if (report.kind === 'departure') {
    const c = record(data.classification);
    add(s('classification.title'), [
      [
        s('classification.employment'),
        typeof c.employment === 'string' ? s(`classification.employments.${c.employment}`) : null,
      ],
      [
        s('classification.purpose'),
        typeof c.purpose === 'string'
          ? `${s(`classification.purposes.${c.purpose}`)}${opt(c.purposeOther) ? ` – ${String(c.purposeOther)}` : ''}`
          : null,
      ],
      [s('classification.charterReference'), opt(c.charterReference)],
      [
        s('classification.euRoute'),
        typeof c.euRoute === 'string' ? s(`classification.euRoutes.${c.euRoute}`) : null,
      ],
    ]);
    add(s('decision.title'), [
      [
        s('decision.question'),
        typeof data.voyageAction === 'string' ? s(`decision.actions.${data.voyageAction}`) : null,
      ],
      [s('decision.reason'), opt(data.noVoyageReason)],
    ]);
    const checklist = record(data.checklist);
    const defects = record(data.openDefects);
    const items = rows(defects.items).map((i) => text(i.label));
    add(s('finalCheck.title'), [
      [
        s('finalCheck.checklist'),
        typeof checklist.status === 'string' ? s(`finalCheck.statuses.${checklist.status}`) : null,
      ],
      [s('finalCheck.record'), opt(checklist.formRecordLabel)],
      [s('finalCheck.checklistRemarks'), opt(checklist.remarks)],
      [s('finalCheck.defects'), items.length > 0 ? items.join('; ') : null],
      [s('finalCheck.restrictions'), opt(defects.remarks)],
    ]);
  }
  return out;
}

/** Dosya adı: "045-26-DEP-01 Departure Report.pdf". */
export function voyageReportPdfFileName(number: string, title: string): string {
  return `${`${number} ${title}`.replace(/[^A-Za-z0-9 ._-]+/g, '-')}.pdf`;
}
